/**
 * 파일 관련 API
 */

import { apiCall, API_BASE_URL, ensureValidToken } from "./base";

/**
 * 파일 경로에 대한 서명된 URL 요청
 */
export const getSignedUrl = async (
  filePath: string,
  userToken: string | null
): Promise<unknown> => {
  const payload = {
    file_path: filePath,
  };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return await apiCall("/raw-files/generate-signed-url", options);
};

/**
 * @deprecated 백엔드에서 Deprecated됨 — 신규 플로우는 temp_upload 사용 후 자동 삭제.
 * Cloud Storage raw 파일 목록 조회
 */
export const getRawFilesFromStorage = async (
  userToken: string | null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  return await apiCall("/raw-files", options);
};

/**
 * 서명된 업로드 URL 요청
 */
export const getSignedUploadUrl = async (
  filename: string,
  contentType: string,
  userToken: string | null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  const endpoint = `/get-upload-url?filename=${encodeURIComponent(
    filename
  )}&content_type=${encodeURIComponent(contentType)}`;
  return await apiCall(endpoint, options);
};

/**
 * 파일 업로드
 */
export const uploadFile = async (formData: FormData): Promise<unknown> => {
  const url = `${API_BASE_URL}/upload-file`;

  try {
    // Firebase 토큰 가져오기
    const token = await ensureValidToken();

    const response = await fetch(url, {
      method: "POST",
      headers: {
        // FormData를 사용할 때는 Content-Type을 설정하지 않습니다
        // 브라우저가 자동으로 multipart/form-data로 설정합니다
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { detail?: string }).detail ||
          `HTTP error! status: ${response.status}`
      );
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("파일 업로드 에러:", error);
    throw error;
  }
};

/**
 * @deprecated 백엔드에서 Deprecated됨 — 신규 플로우는 temp_upload 사용 후 자동 삭제.
 * 파일 삭제
 */
export const deleteRawFile = async (
  filePath: string,
  userToken: string
): Promise<unknown> => {
  const options: RequestInit = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${userToken}`,
    },
    body: JSON.stringify({ file_path: filePath }),
  };
  return await apiCall("/raw-files", options);
};

/**
 * Cloud Storage 기반 변환된 파일 목록 조회
 */
export const getConvertedFiles = async (
  userToken: string | null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  return await apiCall("/converted-files", options);
};

/**
 * metadata.json 파일 조회 (새로운 저장소 구조용) - Main API 서버 사용
 */
export const getMetadataFile = async (
  userToken: string | null,
  uid: string,
  modelName: string
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  // Main API 서버 URL 사용 (files 라우터)
  const mainApiUrl =
    process.env.REACT_APP_MAIN_API_URL ||
    "https://viewrapi-876455037250.asia-northeast3.run.app";
  const endpoint = `${mainApiUrl}/api/files/metadata/${encodeURIComponent(
    uid
  )}/${encodeURIComponent(modelName)}`;

  const url = endpoint;
  const defaultOptions: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Main API 호출 에러:", error);
    throw error;
  }
};

/**
 * 디렉토리 파일 목록 조회 (새로운 저장소 구조용) - Main API 서버 사용
 */
export const getDirectoryFiles = async (
  userToken: string | null,
  uid: string,
  modelName: string
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  // Main API 서버 URL 사용 (files 라우터)
  const mainApiUrl =
    process.env.REACT_APP_MAIN_API_URL ||
    "https://viewrapi-876455037250.asia-northeast3.run.app";
  const endpoint = `${mainApiUrl}/api/files/directory-files/${encodeURIComponent(
    uid
  )}/${encodeURIComponent(modelName)}`;

  const url = endpoint;
  const defaultOptions: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Main API 호출 에러:", error);
    throw error;
  }
};

/**
 * 통합 플랫폼별 파일 확인 (하이브리드 방식) - Main API 서버 사용
 */
export const getPlatformFile = async (
  userToken: string | null,
  uid: string,
  modelName: string
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  // Main API 서버 URL 사용 (files 라우터)
  const mainApiUrl =
    process.env.REACT_APP_MAIN_API_URL ||
    "https://viewrapi-876455037250.asia-northeast3.run.app";
  const endpoint = `${mainApiUrl}/api/files/platform-file/${encodeURIComponent(
    uid
  )}/${encodeURIComponent(modelName)}`;

  const url = endpoint;
  const defaultOptions: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Main API 호출 에러:", error);
    throw error;
  }
};

/**
 * 썸네일 업로드 (Base64/Data URL → GCS)
 */
export const uploadThumbnail = async (
  base64ImageData: string,
  gcsPath: string,
  userToken: string | null,
  uploadOptions?: {
    contentType?: string;
    extension?: "png" | "jpg" | "jpeg";
  }
): Promise<unknown> => {
  const normalizedExtension =
    uploadOptions?.extension === "jpg" || uploadOptions?.extension === "jpeg"
      ? "jpg"
      : "png";
  const contentType =
    uploadOptions?.contentType ||
    (normalizedExtension === "jpg" ? "image/jpeg" : "image/png");

  // trailing slash 제거 — GCS에서 폴더로 오인 방지
  let thumbnailPath = (gcsPath || "").replace(/\/+$/, "").trim();
  // gcsPath에서 .viewr 확장자를 썸네일 확장자로 변경
  if (thumbnailPath.endsWith(".viewr")) {
    thumbnailPath = thumbnailPath.replace(".viewr", `.${normalizedExtension}`);
  } else {
    thumbnailPath = thumbnailPath + `.${normalizedExtension}`;
  }

  const payload = {
    image_data: base64ImageData,
    file_path: thumbnailPath,
    content_type: contentType,
  };

  const requestOptions: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return await apiCall("/api/files/final-files/upload-thumbnail", requestOptions);
};

/**
 * 썸네일 Signed URL 조회
 */
export const getThumbnailSignedUrl = async (
  viewrPath: string,
  userToken: string | null
): Promise<unknown> => {
  const payload = {
    viewr_path: viewrPath,
  };

  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  return await apiCall("/api/files/final-files/thumbnail-signed-url", options);
};

/**
 * 썸네일 업로드 Signed PUT URL 조회 (Unity가 GCS에 직접 PUT)
 */
export const getThumbnailUploadUrl = async (
  viewrPath: string,
  userToken: string | null,
  options?: { contentType?: string }
): Promise<unknown> => {
  const payload = {
    viewr_path: viewrPath,
    content_type: options?.contentType || "image/png",
  };

  const requestOptions: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return await apiCall(
    "/api/files/final-files/thumbnail-upload-url",
    requestOptions,
  );
};

/** Import 직후: Figma 썸네일을 Final GCS에 올리고 공개 HTTPS URL 반환 (Unity WebGL 즉시 표시) */
export const postFigmaHmiThumbnailImmediate = async (params: {
  viewrGcsPath: string;
  spaceIndex: number;
  vseIndex: number;
  hmiIndex: number;
  thumbnailUrl?: string;
  imageData?: string;
}): Promise<{ status?: string; publicUrl?: string }> => {
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify({
      viewr_gcs_path: params.viewrGcsPath,
      space_index: params.spaceIndex,
      vse_index: params.vseIndex,
      hmi_index: params.hmiIndex,
      ...(params.thumbnailUrl ? { thumbnail_url: params.thumbnailUrl } : {}),
      ...(params.imageData ? { image_data: params.imageData } : {}),
    }),
  };
  return (await apiCall(
    "/api/files/final-files/figma-hmi-thumbnail-immediate",
    options,
  )) as { status?: string; publicUrl?: string };
};

/** ProtoPie signed URL/수동 이미지를 사용자 Library용 안정적인 ViewR URL로 복제합니다. */
export const postProtoPieLibraryThumbnail = async (params: {
  prototypeUrl: string;
  thumbnailUrl?: string;
  imageData?: string;
  cloudPieId?: string;
  revisionNo?: number;
  logicalWidth?: number;
  logicalHeight?: number;
}): Promise<{
  status?: string;
  publicUrl?: string;
  width?: number | null;
  height?: number | null;
  thumbnailPixelWidth?: number | null;
  thumbnailPixelHeight?: number | null;
  thumbnailScale?: number | null;
  thumbnailQualityStatus?: string | null;
  dimensionSource?: string | null;
}> => {
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify({
      prototype_url: params.prototypeUrl,
      ...(params.thumbnailUrl ? { thumbnail_url: params.thumbnailUrl } : {}),
      ...(params.imageData ? { image_data: params.imageData } : {}),
      ...(params.cloudPieId ? { cloud_pie_id: params.cloudPieId } : {}),
      ...(params.revisionNo ? { revision_no: params.revisionNo } : {}),
      ...(params.logicalWidth ? { logical_width: params.logicalWidth } : {}),
      ...(params.logicalHeight ? { logical_height: params.logicalHeight } : {}),
    }),
  };
  return (await apiCall("/api/files/protopie/thumbnail", options)) as {
    status?: string;
    publicUrl?: string;
    width?: number | null;
    height?: number | null;
    thumbnailPixelWidth?: number | null;
    thumbnailPixelHeight?: number | null;
    thumbnailScale?: number | null;
    thumbnailQualityStatus?: string | null;
    dimensionSource?: string | null;
  };
};
